import React, {
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from 'react';
import { AppRadioButton } from '../../../general/AppRadioButton/AppRadioButton';
import { AppDropDown } from '../../../general/AppDropDown/AppDropDown';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import {
  CreditApplicationFields,
  CreditApplicationStepField,
  DynamicInvitedSignaturesFields,
  GroupedCreditApplicationFields,
} from '@dill/dill-shared';
import { PlusCircle, Trash } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import SignatureDynamicFields from './SignatureDynamicFields';
import RadioInputs from './RadioInput';
import { AppButton } from '../../../general/AppButton/AppButton';
import AppDatePicker from './AppDatePicker';
import { DillCheckbox } from '../../../general/Dill/DillCheckbox';
import { UploadFileInputs } from './UploadFileInput';
import DocumentField from './DocumentField';
import InviteSignature from './InviteSignature';
import ConditionalOption from './ConditionalOption';
import AppBankReference from './AppBankReference';
import { useAppSelector } from '../../../../redux/store';
import { Mail } from 'react-feather';
import { firebaseLargeFileUploadHelper } from '../../../../utils/helpers';
import StateDropDown from './StateDropDown';
import CheckboxList from './CheckboxList';
import OptionsDropDown from './OptionsDropDown';
import { parseDropdownOptions } from './conditionalOptionUtils';

type SectionItem = {
  id: string;
  sectionType: 'GROUPED' | 'NOT_GROUPED';
  // GROUPED-specific
  groupName?: string;
  groupedFields?: GroupedCreditApplicationFields[];
  isFieldArray?: boolean;
  moreButtonName?: string;
  minimunNumberOfGroups?: number;
  // NOT_GROUPED-specific
  columnType?: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS';
  unGroupedFields?: CreditApplicationFields[];
};

type FieldErrorState = {
  [option: string]: {
    [sectionFieldKey: string]: {
      name?: string;
      id?: string;
    };
  };
};

interface ConditionalSelectedOptionFieldsProps {
  optionList: string;
  onChangeConditionalField: (
    data: Record<string, Record<string, CreditApplicationStepField | null>> | null
  ) => void;
  fieldId: string;
  valueConditionalFields: Record<string, Record<string, CreditApplicationStepField | null>> | null;
  filteredStardardFieldIds: string[];
  currentStepFieldIds: string[];
}

const FIELD_TYPE = [
  'TEXT',
  'NUMBER',
  'CURRENCY',
  'DATE',
  'TEXT_AREA',
  'FILE',
  'DOCUMENT_FIELD',
  'OPTION_FIELD',
  'CONDITIONAL_OPTION',
  'STATE_DROPDOWN',
  'DROPDOWN',
  'CHECKBOX_LIST',
];
const COLUMN_TYPES = ['ONE_COLUMN', 'TWO_COLUMNS', 'THREE_COLUMNS'];

export interface ConditionalSelectedOptionFieldsRef {
  validate: () => boolean;
  onSubmit: () => Record<string, CreditApplicationStepField | null> | null;
  uploadSampleFiles: () => Promise<Record<
    string,
    Record<string, CreditApplicationStepField | null>
  > | null>;
}

const ConditionalSelectedOptionFields = forwardRef<
  ConditionalSelectedOptionFieldsRef,
  ConditionalSelectedOptionFieldsProps
>(
  (
    {
      optionList,
      onChangeConditionalField,
      fieldId,
      valueConditionalFields,
      filteredStardardFieldIds,
      currentStepFieldIds,
    },
    ref
  ) => {
    const [selectedFieldOption, setSelectedFieldOption] = useState<string[]>([]);
    const [sections, setSections] = useState<{ [option: string]: SectionItem[] }>({});
    const [sectionGap, setSectionGap] = useState<{ [option: string]: string }>({});
    const [errorFields, setErrorFields] = useState<string[]>([]);
    const [fieldErrors, setFieldErrors] = useState<FieldErrorState>({});
    const [sendEmail, setSendEmail] = useState<{ [key: string]: boolean }>({});
    const [signatureFields, setSignatureFields] = useState<{
      [key: string]: DynamicInvitedSignaturesFields[];
    }>({});
    const [errorSignatureFields, setErrorSignatureFields] = useState<
      Record<string, Record<string, { name?: string; id?: string }>>
    >({});
    const [selectedSignatureFields, setSelectedSignatureFields] = useState<
      Record<string, DynamicInvitedSignaturesFields[] | null>
    >({});
    const [bankReferenceRequiredFields, setBankReferenceRequiredFields] = useState<
      Record<string, string[]>
    >({});
    const [isIdAutoCompleteOpen, setIsIdAutoCompleteOpen] = useState('');
    const [sampleFiles, setSampleFiles] = useState<Record<string, File>>({});
    // Raw "Option Values" text per field while typing, parsed into an array on blur.
    const [condVisRawText, setCondVisRawText] = useState<Record<string, string>>({});
    const wrapperRef = useRef<HTMLDivElement>(null);
    const initializedForRef = useRef<string>('');

    const currentSupplier = useAppSelector((globalState) => globalState.auth.currentSupplier);

    const list = useMemo(() => {
      return optionList.split(',').map((item) => item.trim());
    }, [optionList]);

    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
          setIsIdAutoCompleteOpen('');
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ── helpers ──────────────────────────────────────────────────────────────

    const getColumnsCount = (type: string) =>
      type === 'ONE_COLUMN' ? 1 : type === 'TWO_COLUMNS' ? 2 : 3;

    const buildUngroupedSectionLayout = (ungroupedSections: SectionItem[]) =>
      ungroupedSections.map((s) => ({
        columnType: s.columnType ?? ('ONE_COLUMN' as const),
        fieldCount: (s.unGroupedFields ?? []).length,
      }));

    const updateSection = (option: string, sectionId: string, updates: Partial<SectionItem>) => {
      setSections((prev) => ({
        ...prev,
        [option]: (prev[option] || []).map((s) => (s.id === sectionId ? { ...s, ...updates } : s)),
      }));
    };

    const addSection = (option: string, type: 'GROUPED' | 'NOT_GROUPED') => {
      const id = `section-${type}-${Date.now()}`;
      const newSection: SectionItem =
        type === 'GROUPED'
          ? {
              id,
              sectionType: 'GROUPED',
              groupName: '',
              groupedFields: [],
              isFieldArray: false,
              minimunNumberOfGroups: 1,
            }
          : {
              id,
              sectionType: 'NOT_GROUPED',
              columnType: 'ONE_COLUMN',
              unGroupedFields: [],
            };
      setSections((prev) => ({ ...prev, [option]: [...(prev[option] || []), newSection] }));
    };

    const removeSection = (option: string, sectionId: string) => {
      setSections((prev) => ({
        ...prev,
        [option]: (prev[option] || []).filter((s) => s.id !== sectionId),
      }));
    };

    const addGroupToSection = (option: string, sectionId: string) => {
      setSections((prev) => {
        const optSections = [...(prev[option] || [])];
        const idx = optSections.findIndex((s) => s.id === sectionId);
        if (idx === -1) return prev;
        const section = { ...optSections[idx] };
        const groups = [...(section.groupedFields || [])];
        groups.push({
          id: `field-${groups.length + 1}-${sectionId}`,
          index: groups.length + 1,
          columnType: 'ONE_COLUMN',
          fields: [],
          groupedPosition: 0,
        });
        section.groupedFields = groups;
        optSections[idx] = section;
        return { ...prev, [option]: optSections };
      });
    };

    const removeGroupFromSection = (option: string, sectionId: string, groupIndex: number) => {
      setSections((prev) => {
        const optSections = [...(prev[option] || [])];
        const idx = optSections.findIndex((s) => s.id === sectionId);
        if (idx === -1) return prev;
        const section = { ...optSections[idx] };
        const groups = [...(section.groupedFields || [])];
        groups.splice(groupIndex, 1);
        section.groupedFields = groups;
        optSections[idx] = section;
        return { ...prev, [option]: optSections };
      });
    };

    const updateGroupField = (
      option: string,
      sectionId: string,
      groupIndex: number,
      fieldIdx: number,
      key: keyof CreditApplicationFields,
      val: CreditApplicationFields[keyof CreditApplicationFields]
    ) => {
      setSections((prev) => {
        const optSections = [...(prev[option] || [])];
        const sIdx = optSections.findIndex((s) => s.id === sectionId);
        if (sIdx === -1) return prev;
        const section = { ...optSections[sIdx] };
        const groups = [...(section.groupedFields || [])];
        const group = { ...groups[groupIndex] };
        const fields = [...(group.fields || [])];
        const existing = fields[fieldIdx] || {
          name: '',
          id: '',
          fromField: 'TEXT' as const,
          isRequired: false,
          prefix: '',
          suffix: '',
        };
        fields[fieldIdx] = { ...existing, [key]: val } as CreditApplicationFields;
        group.fields = fields;
        groups[groupIndex] = group;
        section.groupedFields = groups;
        optSections[sIdx] = section;
        return { ...prev, [option]: optSections };
      });
    };

    const updateGroupColumnType = (
      option: string,
      sectionId: string,
      groupIndex: number,
      columnType: 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS'
    ) => {
      setSections((prev) => {
        const optSections = [...(prev[option] || [])];
        const sIdx = optSections.findIndex((s) => s.id === sectionId);
        if (sIdx === -1) return prev;
        const section = { ...optSections[sIdx] };
        const groups = [...(section.groupedFields || [])];
        const group = { ...groups[groupIndex] };
        const columnCount = getColumnsCount(columnType);
        group.columnType = columnType;
        group.fields = Array(columnCount)
          .fill(null)
          .map(
            (_, i) =>
              group.fields[i] || {
                name: '',
                id: '',
                fromField: 'TEXT' as const,
                isRequired: false,
                prefix: '',
                suffix: '',
              }
          );
        groups[groupIndex] = group;
        section.groupedFields = groups;
        optSections[sIdx] = section;
        return { ...prev, [option]: optSections };
      });
    };

    const updateUngroupedField = (
      option: string,
      sectionId: string,
      fieldIdx: number,
      key: keyof CreditApplicationFields,
      val: CreditApplicationFields[keyof CreditApplicationFields]
    ) => {
      setSections((prev) => {
        const optSections = [...(prev[option] || [])];
        const sIdx = optSections.findIndex((s) => s.id === sectionId);
        if (sIdx === -1) return prev;
        const section = { ...optSections[sIdx] };
        const fields = [...(section.unGroupedFields || [])];
        const existing = fields[fieldIdx] || {
          name: '',
          id: '',
          fromField: 'TEXT' as const,
          isRequired: false,
          prefix: '',
          suffix: '',
        };
        fields[fieldIdx] = { ...existing, [key]: val } as CreditApplicationFields;
        section.unGroupedFields = fields;
        optSections[sIdx] = section;
        return { ...prev, [option]: optSections };
      });
    };

    // ── filter invite user fields ─────────────────────────────────────────────

    const filterInviteUserFields = (fields: CreditApplicationFields[], option: string) => {
      return fields.map((field) => {
        if (field.fromField !== 'INVITE_USER') {
          const { type, minimumCreditAmount, ...filteredField } = field;
          return {
            ...filteredField,
            prefix: filteredField.prefix ?? '',
            suffix: filteredField.suffix ?? '',
          };
        }
        if (field.type === 'AUTHORIZED_AGENT' || field.type === 'PERSONAL') {
          const { minimumCreditAmount, ...filteredField } = field;
          return {
            ...filteredField,
            prefix: filteredField.prefix ?? '',
            suffix: filteredField.suffix ?? '',
            ...(signatureFields[option]?.length > 0 && {
              invitedSignaturesFields: signatureFields[option],
            }),
          };
        }
        return {
          ...field,
          prefix: field.prefix ?? '',
          suffix: field.suffix ?? '',
          ...(signatureFields[option]?.length > 0 && {
            invitedSignaturesFields: signatureFields[option],
          }),
        };
      });
    };

    // ── init from valueConditionalFields ──────────────────────────────────────

    useEffect(() => {
      if (!valueConditionalFields) return;
      // Only re-initialize when fieldId changes (e.g. editing a different field).
      // This breaks the circular loop where onChangeConditionalField → parent
      // updates valueConditionalFields → this effect resets sections → repeat.
      if (initializedForRef.current === fieldId) return;
      const options = valueConditionalFields[fieldId];
      if (!options) return;

      const initialSelected: string[] = [];
      const initialSections: { [option: string]: SectionItem[] } = {};

      for (const key in options) {
        const field = options[key];
        if (!field) continue;
        initialSelected.push(key);

        const sectionItems: SectionItem[] = [];

        if (field.sectionOrder && field.sectionOrder.length > 0) {
          // Restore using saved insertion order.
          // The output effect merges all grouped/ungrouped fields into single flat
          // arrays. Ungrouped sections are split back apart via ungroupedSectionLayout;
          // without it (older saves) only ONE section of each type can be restored —
          // creating more would assign the full merged array to every duplicate.
          const ungroupedLayout = field.ungroupedSectionLayout ?? [];
          let ungroupedLayoutIdx = 0;
          let ungroupedFieldOffset = 0;
          let groupedRestored = false;
          let ungroupedRestored = false;
          for (const sType of field.sectionOrder) {
            if (sType === 'NOT_GROUPED' && ungroupedLayoutIdx < ungroupedLayout.length) {
              const layout = ungroupedLayout[ungroupedLayoutIdx];
              ungroupedLayoutIdx += 1;
              sectionItems.push({
                id: `section-NOT_GROUPED-init-${key}-${sectionItems.length}`,
                sectionType: 'NOT_GROUPED',
                columnType: layout.columnType,
                unGroupedFields: (field.unGroupedFields ?? []).slice(
                  ungroupedFieldOffset,
                  ungroupedFieldOffset + layout.fieldCount
                ),
              });
              ungroupedFieldOffset += layout.fieldCount;
              continue;
            }
            if (sType === 'GROUPED' && !groupedRestored) {
              groupedRestored = true;
              sectionItems.push({
                id: `section-GROUPED-init-${key}-${sectionItems.length}`,
                sectionType: 'GROUPED',
                groupName: field.groupName ?? '',
                groupedFields: field.groupedFields ?? [],
                isFieldArray: field.isFieldArray ?? false,
                moreButtonName: field.moreButtonName ?? '',
                minimunNumberOfGroups: field.minimunNumberOfGroups ?? 1,
              });
            } else if (
              sType === 'NOT_GROUPED' &&
              !ungroupedRestored &&
              ungroupedLayout.length === 0
            ) {
              ungroupedRestored = true;
              sectionItems.push({
                id: `section-NOT_GROUPED-init-${key}-${sectionItems.length}`,
                sectionType: 'NOT_GROUPED',
                columnType: field.columnType ?? 'ONE_COLUMN',
                unGroupedFields: field.unGroupedFields ?? [],
              });
            }
          }
        } else {
          // Legacy: no sectionOrder — restore grouped first, then ungrouped
          if ((field.groupedFields ?? []).length > 0 || field.stepType === 'GROUPED') {
            sectionItems.push({
              id: `section-GROUPED-init-${key}`,
              sectionType: 'GROUPED',
              groupName: field.groupName ?? '',
              groupedFields: field.groupedFields ?? [],
              isFieldArray: field.isFieldArray ?? false,
              moreButtonName: field.moreButtonName ?? '',
              minimunNumberOfGroups: field.minimunNumberOfGroups ?? 1,
            });
          }
          if ((field.unGroupedFields ?? []).length > 0 || field.stepType === 'NOT_GROUPED') {
            sectionItems.push({
              id: `section-NOT_GROUPED-init-${key}`,
              sectionType: 'NOT_GROUPED',
              columnType: field.columnType ?? 'ONE_COLUMN',
              unGroupedFields: field.unGroupedFields ?? [],
            });
          }
        }

        initialSections[key] = sectionItems;

        // Only treat the option as "requires additional info" if there are
        // sections to show. A non-null field with no restorable sections is
        // corrupt/stale data — treat it the same as null (not selected).
        if (sectionItems.length > 0) {
          initialSelected.push(key);
        }

        if (field.sendEmail != null)
          setSendEmail((prev) => ({ ...prev, [key]: field.sendEmail ?? false }));
        if (field.sectionGap)
          setSectionGap((prev) => ({ ...prev, [key]: field.sectionGap as string }));
      }

      initializedForRef.current = fieldId;
      setSections(initialSections);
      setSelectedFieldOption(initialSelected);
    }, [valueConditionalFields, fieldId]);

    // ── output useEffect ──────────────────────────────────────────────────────

    useEffect(() => {
      const data: Record<string, CreditApplicationStepField | null> = {};

      for (const option of list) {
        const optionSections = sections[option] ?? [];

        if (!selectedFieldOption.includes(option) || optionSections.length === 0) {
          data[option] = null;
          continue;
        }
        const groupedSections = optionSections.filter((s) => s.sectionType === 'GROUPED');
        const ungroupedSections = optionSections.filter((s) => s.sectionType === 'NOT_GROUPED');

        const allGroupedFields = groupedSections.flatMap((s) =>
          (s.groupedFields ?? []).map((group) => ({
            ...group,
            fields: filterInviteUserFields(group.fields, option),
          }))
        );

        const allUngroupedFields = ungroupedSections.flatMap((s) =>
          filterInviteUserFields(s.unGroupedFields ?? [], option)
        );

        const firstGrouped = groupedSections[0];
        const firstUngrouped = ungroupedSections[0];
        const hasGrouped = groupedSections.length > 0;

        const optionData: CreditApplicationStepField = {
          columnType: firstUngrouped?.columnType ?? firstGrouped?.columnType ?? 'ONE_COLUMN',
          stepType: hasGrouped ? 'GROUPED' : 'NOT_GROUPED',
          groupedFields: allGroupedFields,
          unGroupedFields: allUngroupedFields,
          groupName: firstGrouped?.groupName ?? '',
          isFieldArray: firstGrouped?.isFieldArray ?? false,
          ...(firstGrouped?.moreButtonName && { moreButtonName: firstGrouped.moreButtonName }),
          sectionOrder: optionSections.map((s) => s.sectionType),
          ungroupedSectionLayout: buildUngroupedSectionLayout(ungroupedSections),
          ...(sectionGap[option] && {
            sectionGap: sectionGap[option] as CreditApplicationStepField['sectionGap'],
          }),
          ...(sendEmail[option] && { sendEmail: sendEmail[option] }),
          ...((bankReferenceRequiredFields[option] ?? []).length > 0 &&
            allUngroupedFields.filter((f) => f.fromField === 'BANK_REFERENCE').length > 0 && {
              bankReferenceRequiredFields: bankReferenceRequiredFields[option],
            }),
          ...(hasGrouped && { minimunNumberOfGroups: firstGrouped?.minimunNumberOfGroups }),
        };

        data[option] = optionData;
      }

      onChangeConditionalField({ [fieldId]: data });
    }, [
      list,
      sections,
      sectionGap,
      signatureFields,
      bankReferenceRequiredFields,
      sendEmail,
      selectedFieldOption,
      fieldId,
    ]);

    // ── validate ──────────────────────────────────────────────────────────────

    const validateSignatureFields = () => {
      const errors: Record<string, Record<string, { name?: string; id?: string }>> = {};
      Object.keys(signatureFields).forEach((optionKey) => {
        const optionErrors: Record<string, { name?: string; id?: string }> = {};
        signatureFields[optionKey].forEach((sig, idx) => {
          const key = `signature-${idx}-${sig.index}`;
          const sigErrors: { name?: string; id?: string } = {};
          if (!sig.name?.trim()) sigErrors.name = 'Field name is required';
          if (!sig.id?.trim()) sigErrors.id = 'Field id is required';
          else if (filteredStardardFieldIds.includes(sig.id))
            sigErrors.id = 'Field id already exists';
          if (Object.keys(sigErrors).length > 0) optionErrors[key] = sigErrors;
        });
        if (Object.keys(optionErrors).length > 0) errors[optionKey] = optionErrors;
      });
      setErrorSignatureFields(errors);
      return Object.keys(errors).length === 0;
    };

    const validateFields = () => {
      const newErrors: string[] = [];
      const newFieldErrors: FieldErrorState = {};

      selectedFieldOption.forEach((option) => {
        const optionSections = sections[option] ?? [];

        if (optionSections.length === 0) {
          newErrors.push(`${option}-no-sections`);
        }

        optionSections.forEach((section) => {
          const sId = section.id;

          if (section.sectionType === 'GROUPED') {
            if (!section.groupName) newErrors.push(`groupName-${sId}`);

            (section.groupedFields ?? []).forEach((group) => {
              group.fields.forEach((field, idx) => {
                const fKey = `${sId}-${group.id}-${idx}`;
                if (!newFieldErrors[option]) newFieldErrors[option] = {};
                if (!field.name?.trim()) {
                  newFieldErrors[option][fKey] = {
                    ...newFieldErrors[option][fKey],
                    name: 'Field name is required',
                  };
                }
                if (!field.id?.trim()) {
                  newFieldErrors[option][fKey] = {
                    ...newFieldErrors[option][fKey],
                    id: 'Field ID is required',
                  };
                } else if (currentStepFieldIds.includes(field.id)) {
                  newFieldErrors[option][fKey] = {
                    ...newFieldErrors[option][fKey],
                    id: 'Field ID already exists',
                  };
                }
                if (
                  (field.fromField === 'INVITE_USER' && field.type === 'GUARANTOR') ||
                  field.type === 'CORPORATE_GUARANTOR'
                ) {
                  if (!field.minimumCreditAmount || field.minimumCreditAmount < 1) {
                    newErrors.push('minimumCreditAmount');
                  }
                }
              });
            });
          }

          if (section.sectionType === 'NOT_GROUPED') {
            if (!section.columnType) newErrors.push(`fieldColumn-${sId}`);

            (section.unGroupedFields ?? []).forEach((field, idx) => {
              const fKey = `${sId}-ungrouped-${idx}`;
              if (!newFieldErrors[option]) newFieldErrors[option] = {};
              if (!field.name?.trim()) {
                newFieldErrors[option][fKey] = {
                  ...newFieldErrors[option][fKey],
                  name: 'Field name is required',
                };
              }
              if (!field.id?.trim()) {
                newFieldErrors[option][fKey] = {
                  ...newFieldErrors[option][fKey],
                  id: 'Field ID is required',
                };
              } else if (currentStepFieldIds.includes(field.id)) {
                newFieldErrors[option][fKey] = {
                  ...newFieldErrors[option][fKey],
                  id: 'Field ID already exists',
                };
              }
            });
          }
        });
      });

      setErrorFields(newErrors);
      setFieldErrors(newFieldErrors);

      const signaturesValid = validateSignatureFields();
      const hasFieldErrors = Object.values(newFieldErrors).some((optErrors) =>
        Object.values(optErrors).some((fe) => !!fe.name || !!fe.id)
      );

      return newErrors.length === 0 && !hasFieldErrors && signaturesValid;
    };

    // ── handleSubmit ──────────────────────────────────────────────────────────

    const handleSubmit = () => {
      if (!validateFields() || !validateSignatureFields()) return null;

      const data: Record<string, CreditApplicationStepField | null> = {};

      for (const option of list) {
        if (!selectedFieldOption.includes(option)) {
          data[option] = null;
          continue;
        }

        const optionSections = sections[option] ?? [];
        const groupedSections = optionSections.filter((s) => s.sectionType === 'GROUPED');
        const ungroupedSections = optionSections.filter((s) => s.sectionType === 'NOT_GROUPED');

        const allGroupedFields = groupedSections.flatMap((s) =>
          (s.groupedFields ?? []).map((group) => ({
            ...group,
            fields: filterInviteUserFields(group.fields, option),
          }))
        );

        const allUngroupedFields = ungroupedSections.flatMap((s) =>
          filterInviteUserFields(s.unGroupedFields ?? [], option)
        );

        const firstGrouped = groupedSections[0];
        const firstUngrouped = ungroupedSections[0];
        const hasGrouped = groupedSections.length > 0;

        const optionData: CreditApplicationStepField = {
          columnType: firstUngrouped?.columnType ?? firstGrouped?.columnType ?? 'ONE_COLUMN',
          stepType: hasGrouped ? 'GROUPED' : 'NOT_GROUPED',
          groupedFields: allGroupedFields,
          unGroupedFields: allUngroupedFields,
          groupName: firstGrouped?.groupName ?? '',
          isFieldArray: firstGrouped?.isFieldArray ?? false,
          ...(firstGrouped?.moreButtonName && { moreButtonName: firstGrouped.moreButtonName }),
          ...(sendEmail[option] && { sendEmail: sendEmail[option] }),
          ...((bankReferenceRequiredFields[option] ?? []).length > 0 &&
            allUngroupedFields.filter((f) => f.fromField === 'BANK_REFERENCE').length > 0 && {
              bankReferenceRequiredFields: bankReferenceRequiredFields[option],
            }),
          sectionOrder: optionSections.map((s) => s.sectionType),
          ungroupedSectionLayout: buildUngroupedSectionLayout(ungroupedSections),
          ...(sectionGap[option] && {
            sectionGap: sectionGap[option] as CreditApplicationStepField['sectionGap'],
          }),
          ...(hasGrouped && { minimunNumberOfGroups: firstGrouped?.minimunNumberOfGroups }),
        };

        data[option] = optionData;
      }

      return data;
    };

    const uploadSampleFiles = async (): Promise<Record<
      string,
      Record<string, CreditApplicationStepField | null>
    > | null> => {
      // Upload all pending files and build a url map keyed by fieldKey
      const urlMap: Record<string, string> = {};
      for (const [fk, file] of Object.entries(sampleFiles)) {
        const url = await firebaseLargeFileUploadHelper({
          blobUrl: URL.createObjectURL(file),
          fileNameWithExtension: file.name,
          storagePath: `creditApplicationFiles/sampleFiles/${
            currentSupplier?.id || 'unknown'
          }/${Date.now()}_${file.name}`,
        });
        if (url) urlMap[fk] = url;
      }

      if (Object.keys(urlMap).length === 0 && Object.keys(sampleFiles).length === 0) return null;

      // Build output data with URLs injected (mirrors handleSubmit logic)
      const data: Record<string, CreditApplicationStepField | null> = {};
      for (const option of list) {
        if (!selectedFieldOption.includes(option)) {
          data[option] = null;
          continue;
        }

        const optionSections = sections[option] ?? [];
        const groupedSections = optionSections.filter((s) => s.sectionType === 'GROUPED');
        const ungroupedSections = optionSections.filter((s) => s.sectionType === 'NOT_GROUPED');

        const allGroupedFields = groupedSections.flatMap((s) =>
          (s.groupedFields ?? []).map((group) => ({
            ...group,
            fields: filterInviteUserFields(
              group.fields.map((field, fIdx) => {
                const fk = `${s.id}-${group.id}-${fIdx}`;
                return urlMap[fk]
                  ? { ...field, sampleFile: { ...field.sampleFile, url: urlMap[fk] } }
                  : field;
              }),
              option
            ),
          }))
        );

        const allUngroupedFields = ungroupedSections.flatMap((s) =>
          filterInviteUserFields(
            (s.unGroupedFields ?? []).map((field, fIdx) => {
              const fk = `${s.id}-ungrouped-${fIdx}`;
              return urlMap[fk]
                ? { ...field, sampleFile: { ...field.sampleFile, url: urlMap[fk] } }
                : field;
            }),
            option
          )
        );

        const firstGrouped = groupedSections[0];
        const firstUngrouped = ungroupedSections[0];
        const hasGrouped = groupedSections.length > 0;

        const optionData: CreditApplicationStepField = {
          columnType: firstUngrouped?.columnType ?? firstGrouped?.columnType ?? 'ONE_COLUMN',
          stepType: hasGrouped ? 'GROUPED' : 'NOT_GROUPED',
          groupedFields: allGroupedFields,
          unGroupedFields: allUngroupedFields,
          groupName: firstGrouped?.groupName ?? '',
          isFieldArray: firstGrouped?.isFieldArray ?? false,
          ...(firstGrouped?.moreButtonName && { moreButtonName: firstGrouped.moreButtonName }),
          ...(sendEmail[option] && { sendEmail: sendEmail[option] }),
          ...((bankReferenceRequiredFields[option] ?? []).length > 0 &&
            allUngroupedFields.filter((f) => f.fromField === 'BANK_REFERENCE').length > 0 && {
              bankReferenceRequiredFields: bankReferenceRequiredFields[option],
            }),
          sectionOrder: optionSections.map((s) => s.sectionType),
          ungroupedSectionLayout: buildUngroupedSectionLayout(ungroupedSections),
          ...(sectionGap[option] && {
            sectionGap: sectionGap[option] as CreditApplicationStepField['sectionGap'],
          }),
          ...(hasGrouped && { minimunNumberOfGroups: firstGrouped?.minimunNumberOfGroups }),
        };

        data[option] = optionData;
      }

      return { [fieldId]: data };
    };

    useImperativeHandle(ref, () => ({
      validate: validateFields,
      onSubmit: handleSubmit,
      uploadSampleFiles,
    }));

    // ── field inputs renderer ─────────────────────────────────────────────────

    const filteredIdSuggestionsOptions = (text: string) => {
      if (!text) return [];
      return filteredStardardFieldIds.filter((o) =>
        o.toLowerCase().includes(text.trim().toLowerCase())
      );
    };

    const renderFieldInputs = (
      option: string,
      sectionId: string,
      fields: CreditApplicationFields[],
      groupIndex: number,
      isGrouped: boolean,
      onChange: <K extends keyof CreditApplicationFields>(
        fieldIndex: number,
        key: K,
        value: CreditApplicationFields[K]
      ) => void
    ) => {
      return fields.map((field, idx) => {
        const fieldKey = isGrouped
          ? `${sectionId}-${
              (sections[option]?.find((s) => s.id === sectionId) as any)?.groupedFields?.[
                groupIndex
              ]?.id
            }-${idx}`
          : `${sectionId}-ungrouped-${idx}`;
        const idSuggestedOptions = filteredIdSuggestionsOptions(field.id);

        return (
          <div key={idx} className="w-full rounded GREY_300-BORDER border p-2">
            <div className="w-full mt-2 mb-4">
              <AppInputField
                isRequired
                placeholder="Enter field name"
                label="Field Name"
                value={field.name}
                errorText={fieldErrors[option]?.[fieldKey]?.name || ''}
                onTextChange={(text) => onChange(idx, 'name', text)}
              />
            </div>
            <div className="w-full mt-2 mb-4 relative" ref={wrapperRef}>
              <AppInputField
                isRequired
                placeholder="Enter field id"
                label="Field Id"
                value={field.id}
                onFocus={() => setIsIdAutoCompleteOpen(`focus-id-${fieldKey}`)}
                onMouseUp={() => setIsIdAutoCompleteOpen(`focus-id-${fieldKey}`)}
                errorText={fieldErrors[option]?.[fieldKey]?.id || ''}
                onBlur={() => {
                  if (field.id?.trim() !== field.id) onChange(idx, 'id', field.id?.trim() ?? '');
                }}
                onTextChange={(text) => {
                  onChange(idx, 'id', text);
                  setFieldErrors((prev) => ({
                    ...prev,
                    [option]: {
                      ...prev[option],
                      [fieldKey]: { ...prev[option]?.[fieldKey], id: '' },
                    },
                  }));
                }}
              />
              {isIdAutoCompleteOpen === `focus-id-${fieldKey}` && idSuggestedOptions.length > 0 && (
                <div className="absolute w-full mt-1 bg-white border rounded-lg shadow-md z-50">
                  <div className="p-2 max-h-60 overflow-y-auto">
                    {idSuggestedOptions.map((opt, i) => (
                      <div
                        key={`${opt}_${i}-id-option`}
                        onMouseDown={() => {
                          onChange(idx, 'id', opt);
                          setIsIdAutoCompleteOpen('');
                        }}
                        className="p-1 hover:bg-gray-100 cursor-pointer text-xs">
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="w-full my-2">
              <AppDropDown
                label="Field Type"
                isRequired
                value={field.fromField}
                items={FIELD_TYPE.map((type) => ({
                  label: type.replaceAll('_', ' '),
                  value: type,
                }))}
                onSelectChange={(item) => {
                  if (item) {
                    onChange(idx, 'fromField', item.value);
                    onChange(idx, 'isRequired', false);
                    onChange(idx, 'isHashed', false);
                    if (field.fromField === 'INVITE_USER') {
                      onChange(idx, 'minimumCreditAmount', 0);
                      onChange(idx, 'type', 'AUTHORIZED_AGENT');
                    }
                  }
                }}
              />
            </div>
            {field.fromField === 'CONDITIONAL_OPTION' && (
              <div className="w-full my-2">
                <AppDropDown
                  label="Option Type"
                  isRequired
                  value={field.optionType ?? ''}
                  items={[
                    { label: 'Text', value: 'text' },
                    { label: 'File', value: 'file' },
                  ]}
                  onSelectChange={(item) => {
                    if (item) onChange(idx, 'optionType', item.value);
                  }}
                />
              </div>
            )}
            {(field.fromField === 'TEXT' ||
              field.fromField === 'NUMBER' ||
              field.fromField === 'CURRENCY') && (
              <>
                <div className="my-2">
                  <AppInputField
                    label="Prefix"
                    value={field.prefix}
                    onTextChange={(text) => onChange(idx, 'prefix', text)}
                  />
                </div>
                <div className="my-2">
                  <AppInputField
                    label="Suffix"
                    value={field.suffix}
                    onTextChange={(text) => onChange(idx, 'suffix', text)}
                  />
                </div>
              </>
            )}
            {field.fromField === 'CURRENCY' && (
              <div className="my-2">
                <AppDropDown
                  label="Decimal Places"
                  value={String(
                    (field as any).currencyDecimalPlaces !== undefined
                      ? (field as any).currencyDecimalPlaces
                      : 2
                  )}
                  items={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => ({
                    label: String(n),
                    value: String(n),
                  }))}
                  onSelectChange={(item) => {
                    if (item)
                      (onChange as any)(idx, 'currencyDecimalPlaces', parseInt(item.value, 10));
                  }}
                />
              </div>
            )}
            {field.fromField === 'DATE' && (
              <div className="my-2">
                <AppDropDown
                  label="Date Format"
                  value={field.datePickerFormat ?? 'FULL_DATE'}
                  items={[
                    { label: 'Full Date (MM/DD/YYYY)', value: 'FULL_DATE' },
                    { label: 'Month & Year (MM/YYYY)', value: 'MONTH_YEAR' },
                  ]}
                  onSelectChange={(item) => {
                    if (item)
                      onChange(idx, 'datePickerFormat', item.value as 'FULL_DATE' | 'MONTH_YEAR');
                  }}
                />
              </div>
            )}
            {field.fromField === 'CONDITIONAL_OPTION' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Secondary Text"
                  value={field.secondaryText || ''}
                  onTextChange={(text) => onChange(idx, 'secondaryText', text)}
                />
              </div>
            )}
            {(field.fromField === 'OPTION_FIELD' || field.fromField === 'CHECKBOX_LIST') && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Option List"
                  value={field.optionList || ''}
                  onTextChange={(text) => onChange(idx, 'optionList', text)}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Add option list separed by comma
                </p>
              </div>
            )}
            {field.fromField === 'DROPDOWN' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Options"
                  value={field.optionList || ''}
                  onTextChange={(text) => onChange(idx, 'optionList', text)}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Add option list separated by &quot;::&quot;
                </p>
              </div>
            )}
            {(field.id?.includes('supplier-branches') ||
              field.id?.includes('supplier-sales-reps')) &&
              (field.fromField === 'OPTION_FIELD' ||
                field.fromField === 'CHECKBOX_LIST' ||
                field.fromField === 'DROPDOWN') && (
                <div className="my-2 flex flex-col gap-2">
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <AppDropDown
                        label="Value to save"
                        value={field.optionValueSource ?? 'NAME'}
                        items={[
                          { label: 'Name', value: 'NAME' },
                          { label: 'ERP ID', value: 'ERP_ID' },
                        ]}
                        onSelectChange={(item) => {
                          if (item)
                            onChange(idx, 'optionValueSource', item.value as 'NAME' | 'ERP_ID');
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <AppDropDown
                        label="Label to show"
                        value={field.optionLabelSource ?? 'NAME'}
                        items={[
                          { label: 'Name', value: 'NAME' },
                          { label: 'ERP ID', value: 'ERP_ID' },
                        ]}
                        onSelectChange={(item) => {
                          if (item)
                            onChange(idx, 'optionLabelSource', item.value as 'NAME' | 'ERP_ID');
                        }}
                      />
                    </div>
                  </div>
                  <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                    ERP ID avoids ambiguity if two branches/reps share a name. Value and label are
                    independent — e.g. save the ERP ID while still showing the name.
                  </p>
                </div>
              )}
            {field.id?.includes('supplier-sales-reps') &&
              (field.fromField === 'OPTION_FIELD' ||
                field.fromField === 'CHECKBOX_LIST' ||
                field.fromField === 'DROPDOWN') && (
                <div className="my-2 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <DillCheckbox
                      isChecked={field.houseRepsOnly ?? false}
                      onChange={(checked: boolean) => onChange(idx, 'houseRepsOnly', checked)}
                    />
                    <span
                      className="text-sm cursor-pointer"
                      onClick={() => onChange(idx, 'houseRepsOnly', !field.houseRepsOnly)}>
                      Only show House Reps
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <AppInputField
                        label="Field label when a House Rep is selected"
                        value={field.houseRepFieldLabel || ''}
                        placeholder="e.g. House Account Id"
                        onTextChange={(text) => onChange(idx, 'houseRepFieldLabel', text)}
                      />
                    </div>
                    <div className="flex-1">
                      <AppInputField
                        label="Field label otherwise"
                        value={field.nonHouseRepFieldLabel || ''}
                        placeholder="e.g. Sales Rep Id"
                        onTextChange={(text) => onChange(idx, 'nonHouseRepFieldLabel', text)}
                      />
                    </div>
                  </div>
                  <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                    Leave both blank to keep the Field Name above fixed regardless of which rep is
                    selected.
                  </p>
                </div>
              )}
            {field.fromField === 'OPTION_FIELD' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  label="Option Descriptions"
                  value={field.optionDescriptions || ''}
                  onTextChange={(text) => onChange(idx, 'optionDescriptions', text)}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Optional. Add one description per option, separated by &quot;;;&quot;, in the same
                  order as the Option List above.
                </p>
              </div>
            )}
            {field.fromField === 'INVITE_USER' && (
              <div className="my-2 flex items-center">
                <AppDropDown
                  label="Type of signer"
                  isRequired
                  value={field?.type ?? ''}
                  errorText={errorFields.includes('groupType') ? 'Please select' : ''}
                  items={[
                    'AUTHORIZED_AGENT',
                    'GUARANTOR',
                    'OTHER',
                    'PERSONAL',
                    'CORPORATE_GUARANTOR',
                  ].map((type) => ({
                    label: type.replaceAll('_', ' '),
                    value: type,
                  }))}
                  onSelectChange={(item) => {
                    if (item)
                      onChange(
                        idx,
                        'type',
                        item.value as
                          | 'AUTHORIZED_AGENT'
                          | 'GUARANTOR'
                          | 'OTHER'
                          | 'PERSONAL'
                          | 'CORPORATE_GUARANTOR'
                      );
                  }}
                />
              </div>
            )}
            {field.fromField === 'INVITE_USER' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  isRequired
                  label="Who needs to sign"
                  value={field.whoNeedsToSign || ''}
                  onTextChange={(text) => onChange(idx, 'whoNeedsToSign', text)}
                />
                <p className="text-xs ml-1 cursor-pointer text-left PRIMARY_300-CLR">
                  Add titles of who needs to sign separed by comma
                </p>
              </div>
            )}
            {field.fromField === 'INVITE_USER' && (
              <div className="my-2 flex flex-col">
                <AppInputField
                  isRequired
                  label="Minimum number of signatures"
                  value={field.minimumNumberOfSignatures || ''}
                  onTextChange={(text) => onChange(idx, 'minimumNumberOfSignatures', text)}
                />
              </div>
            )}
            {field.fromField !== 'DOCUMENT_FIELD' &&
              field.fromField !== 'INVITE_USER' &&
              field.fromField !== 'BANK_REFERENCE' && (
                <div className="my-2 flex items-center">
                  <AppRadioButton
                    isChecked={field.isRequired}
                    onClick={(val: boolean) => onChange(idx, 'isRequired', val)}
                  />
                  <p
                    className="text-sm ml-1 cursor-pointer"
                    onClick={() => onChange(idx, 'isRequired', !field.isRequired)}>
                    Is Field Required
                  </p>
                </div>
              )}
            {field.fromField === 'BANK_REFERENCE' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.onlyShowPlaidOption}
                  onClick={(val: boolean) => onChange(idx, 'onlyShowPlaidOption', val)}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => onChange(idx, 'onlyShowPlaidOption', !field.onlyShowPlaidOption)}>
                  Show only plaid option
                </p>
              </div>
            )}
            {field.fromField === 'BANK_REFERENCE' && (
              <div className="my-2 items-center text-xs">
                <div>
                  Apart from Bank and Name and Account Number what other field will be required for
                  the user to fill manually
                </div>
                <div className="flex gap-4 items-center">
                  {[
                    { name: 'Address', value: 'address' },
                    { name: 'Phone Number', value: 'phoneNumber' },
                    { name: 'Contact Person', value: 'contactPerson' },
                  ].map((opt) => (
                    <div className="flex my-1 items-center" key={`item-${opt.value}`}>
                      <AppRadioButton
                        isChecked={(bankReferenceRequiredFields[option] || []).includes(opt.value)}
                        onClick={(val: boolean) => {
                          if (val) {
                            setBankReferenceRequiredFields((prev) => ({
                              ...prev,
                              [option]: [...(prev[option] || []), opt.value],
                            }));
                          } else {
                            setBankReferenceRequiredFields((prev) => ({
                              ...prev,
                              [option]: (prev[option] || []).filter((f) => f !== opt.value),
                            }));
                          }
                        }}
                      />
                      <p
                        className="text-xs ml-1 cursor-pointer"
                        onClick={() => {
                          if (!(bankReferenceRequiredFields?.[option] || []).includes(opt.value)) {
                            setBankReferenceRequiredFields((prev) => ({
                              ...prev,
                              [option]: [...(prev[option] || []), opt.value],
                            }));
                          } else {
                            setBankReferenceRequiredFields((prev) => ({
                              ...prev,
                              [option]: (prev[option] || []).filter((f) => f !== opt.value),
                            }));
                          }
                        }}>
                        {opt.name}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {field.fromField === 'TEXT' && (
              <div className="my-2">
                <AppDropDown
                  label="Validation Type"
                  value={field.validationType ?? 'NONE'}
                  items={[
                    { label: 'None', value: 'NONE' },
                    { label: 'Tax ID / EIN (XX-XXXXXXX)', value: 'EIN' },
                    { label: 'SSN (XXX-XX-XXXX)', value: 'SSN' },
                    { label: 'Phone Number (XXX-XXX-XXXX)', value: 'PHONE' },
                  ]}
                  onSelectChange={(item) => {
                    if (item)
                      onChange(
                        idx,
                        'validationType',
                        item.value as 'NONE' | 'EIN' | 'SSN' | 'PHONE'
                      );
                  }}
                />
              </div>
            )}
            {(field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') && (
              <div className="my-2">
                <AppInputField
                  label="Max Length"
                  inputType="number"
                  placeholder="No limit"
                  value={field.maxLength !== undefined ? field.maxLength : ''}
                  onTextChange={(text) => {
                    onChange(idx, 'maxLength', text === '' ? undefined : Number(text));
                  }}
                />
              </div>
            )}
            {field.fromField === 'TEXT' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isFieldEmail || false}
                  onClick={(val: boolean) => {
                    onChange(idx, 'isFieldEmail', val);
                    if (!val) onChange(idx, 'emailValidationType', undefined);
                  }}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => {
                    const next = !field.isFieldEmail;
                    onChange(idx, 'isFieldEmail', next);
                    if (!next) onChange(idx, 'emailValidationType', undefined);
                  }}>
                  Is field an email
                </p>
              </div>
            )}
            {field.fromField === 'TEXT' && field.isFieldEmail && (
              <div className="my-2">
                <AppDropDown
                  label="Email Validation Type"
                  value={field.emailValidationType ?? 'SINGLE'}
                  items={[
                    { label: 'Single email', value: 'SINGLE' },
                    { label: 'Multiple emails (comma or semicolon separated)', value: 'MULTIPLE' },
                  ]}
                  onSelectChange={(item) => {
                    if (item)
                      onChange(idx, 'emailValidationType', item.value as 'SINGLE' | 'MULTIPLE');
                  }}
                />
              </div>
            )}
            {(field.fromField === 'TEXT' || field.fromField === 'NUMBER') && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isHashed || false}
                  onClick={(val: boolean) => onChange(idx, 'isHashed', val)}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => onChange(idx, 'isHashed', !field.isHashed)}>
                  Is Field Hashed
                </p>
              </div>
            )}
            {!isGrouped && (
              <div className="my-2">
                <AppInputField
                  label="Border Group: Last Field Id (optional)"
                  value={field.borderId || ''}
                  onTextChange={(text) => onChange(idx, 'borderId', text || null)}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Set this on the FIRST field of a group to the id of the LAST field in that group.
                  Everything between them (inclusive) will be boxed together.
                </p>
              </div>
            )}
            {/* Show this field only when another field (in this option or anywhere on the form) has a specific value */}
            <div className="my-2 border border-gray-100 p-2 rounded">
              <p className="text-xs font-medium text-gray-500 mb-1">Show this field only if…</p>
              <div className="flex flex-col gap-2">
                <AppInputField
                  label="Source Field ID"
                  placeholder="e.g. has-business-license"
                  value={field.conditionalVisibility?.sourceFieldId ?? ''}
                  onTextChange={(text) => {
                    // Keep any option values typed before the source id was filled in.
                    const current = field.conditionalVisibility ?? {
                      sourceFieldId: '',
                      sourceOptionValues: (condVisRawText[fieldKey] ?? '')
                        .split(',')
                        .map((v: string) => v.trim())
                        .filter(Boolean),
                    };
                    onChange(
                      idx,
                      'conditionalVisibility',
                      text ? { ...current, sourceFieldId: text } : null
                    );
                  }}
                />
                <AppInputField
                  label="Option Values (comma-separated)"
                  placeholder="e.g. Yes, Maybe"
                  value={
                    condVisRawText[fieldKey] ??
                    (field.conditionalVisibility?.sourceOptionValues ?? []).join(', ')
                  }
                  onTextChange={(text) => {
                    setCondVisRawText((prev) => ({ ...prev, [fieldKey]: text }));
                  }}
                  onBlur={() => {
                    const raw = condVisRawText[fieldKey];
                    if (raw === undefined || !field.conditionalVisibility) return;
                    onChange(idx, 'conditionalVisibility', {
                      ...field.conditionalVisibility,
                      sourceOptionValues: raw
                        .split(',')
                        .map((v: string) => v.trim())
                        .filter(Boolean),
                    });
                  }}
                />
              </div>
              {field.conditionalVisibility?.sourceFieldId &&
                (field.conditionalVisibility.sourceOptionValues ?? []).length > 0 && (
                  <p className="text-xs mt-1 PRIMARY_300-CLR">
                    This field shows only when &quot;{field.conditionalVisibility.sourceFieldId}
                    &quot; is one of: &quot;
                    {field.conditionalVisibility.sourceOptionValues.join('", "')}&quot;
                  </p>
                )}
            </div>
            {field.fromField === 'FILE' && (
              <div className="my-2 border-t border-gray-100 pt-2">
                <p className="text-xs font-medium text-gray-500 mb-1">
                  Sample File{field.name ? ` — ${field.name}` : ''}
                </p>
                <UploadFileInputs
                  label={field.name ? `${field.name} (sample)` : 'Upload sample file'}
                  onSelectFile={({ file }) => {
                    setSampleFiles((prev) => ({ ...prev, [fieldKey]: file }));
                    onChange(idx, 'sampleFile', {
                      name: file.name,
                      fileType: file.name.split('.').pop() || '',
                      url: field.sampleFile?.url,
                    });
                  }}
                  selectedFileName={sampleFiles[fieldKey]?.name || field.sampleFile?.name}
                />
                {field.sampleFile?.url && !sampleFiles[fieldKey] && (
                  <a
                    href={field.sampleFile.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs PRIMARY_300-CLR underline mt-1 inline-block">
                    Preview / Download sample
                  </a>
                )}
              </div>
            )}
            {field.fromField === 'INVITE_USER' && field?.type === 'GUARANTOR' && (
              <div className="my-2 flex items-center">
                <AppInputField
                  label="Minimum Credit Amount"
                  value={field?.minimumCreditAmount?.toString() ?? ''}
                  onTextChange={(text) => onChange(idx, 'minimumCreditAmount', Number(text))}
                  inputType="number"
                  isRequired
                  placeholder="Enter minimum credit amount"
                  onBlur={() => {
                    if (field?.minimumCreditAmount && field?.minimumCreditAmount < 1) {
                      onChange(idx, 'minimumCreditAmount', 1);
                    }
                  }}
                  errorText={
                    errorFields.includes('minimumCreditAmount')
                      ? 'Minimum credit amount is required'
                      : ''
                  }
                />
              </div>
            )}
            {field.fromField === 'INVITE_USER' && (
              <div className="my-2 flex items-center">
                <SignatureDynamicFields
                  onSubmit={(data: { fields: DynamicInvitedSignaturesFields[] }) => {
                    setSignatureFields((prev) => ({ ...prev, [field.id]: data.fields }));
                  }}
                  errorSignatureFields={errorSignatureFields[option]}
                  valueData={signatureFields[option]}
                  selectedSignatureFields={selectedSignatureFields[option]}
                  filteredStardardFieldIds={filteredStardardFieldIds}
                />
              </div>
            )}
            {field.fromField === 'DOCUMENT_FIELD' && (
              <>
                <div className="my-2 flex items-center flex-col">
                  <AppInputField
                    inputType="textarea"
                    label="Document Text"
                    value={field.documentText || ''}
                    onTextChange={(text) => onChange(idx, 'documentText', text)}
                  />
                </div>
                <div className="my-2 flex items-center flex-col">
                  <AppInputField
                    label="Font Size"
                    value={field.fontSize || ''}
                    onTextChange={(text) => onChange(idx, 'fontSize', text)}
                  />
                </div>
                <div className="my-2 flex items-center flex-col justify-start">
                  <p>Font Weight</p>
                  <RadioInputs
                    id={`fontWeight-${idx}`}
                    selectedOption={field.fontWeight || 'font-normal'}
                    options={['font-thin', 'font-normal', 'font-semibold', 'font-bold']}
                    onChange={({ value }) => onChange(idx, 'fontWeight', value)}
                    className="gap-2 flex items-center"
                  />
                </div>
              </>
            )}
            {(field.fromField === 'DOCUMENT_FIELD' || field.fromField === 'CHECK_BOX') && (
              <div className="my-2 flex items-center flex-col justify-start">
                <p>How are the fields aligned in the document? (LEFT, RIGHT, CENTER)</p>
                <RadioInputs
                  id={`alignment-${idx}`}
                  selectedOption={field.alignment || 'LEFT'}
                  options={['LEFT', 'CENTER', 'RIGHT']}
                  onChange={({ value }) =>
                    onChange(idx, 'alignment', value as 'LEFT' | 'RIGHT' | 'CENTER')
                  }
                  className="gap-2 flex items-center"
                />
              </div>
            )}
            {field.fromField === 'OPTION_FIELD' && (
              <div className="my-2 flex items-center">
                <AppRadioButton
                  isChecked={field.isRow || false}
                  onClick={(val: boolean) => onChange(idx, 'isRow', val)}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() => onChange(idx, 'isRow', !field.isRow)}>
                  Are options in a row or column
                </p>
              </div>
            )}
          </div>
        );
      });
    };

    // ── preview renderer ──────────────────────────────────────────────────────

    const renderPreview = (fields: CreditApplicationFields[]) => {
      return fields.map((field, idx) => {
        let fieldComponent = null;

        switch (field.fromField) {
          case 'TEXT':
          case 'NUMBER':
          case 'CURRENCY':
          case 'TEXT_AREA':
            fieldComponent = (
              <AppInputField
                label={field.name}
                inputType={
                  field.fromField === 'TEXT_AREA'
                    ? 'textarea'
                    : field.fromField === 'NUMBER' || field.fromField === 'CURRENCY'
                    ? 'number'
                    : 'text'
                }
                value=""
                isRequired={field.isRequired}
                suffix={field.suffix}
                prefix={field.prefix}
                onTextChange={() => {}}
                dataPrivate={field?.isHashed ? true : false}
              />
            );
            break;
          case 'DATE':
            fieldComponent = (
              <AppDatePicker
                label={field.name}
                selectedDate={new Date()}
                isRequired={field.isRequired}
                onDateSelected={() => {}}
              />
            );
            break;
          case 'CHECK_BOX':
            fieldComponent = (
              <div
                className={`my-1 flex items-center w-full ${
                  field.alignment === 'RIGHT'
                    ? 'justify-end'
                    : field.alignment === 'CENTER'
                    ? 'justify-center'
                    : 'justify-start'
                }`}>
                <DillCheckbox isChecked={false} />
                <div className="ml-2 text-sm">{field.name}</div>
              </div>
            );
            break;
          case 'FILE':
            fieldComponent = (
              <div className="my-1 flex items-center">
                <UploadFileInputs
                  isRequired={field.isRequired}
                  label={field.name}
                  onSelectFile={() => {}}
                />
              </div>
            );
            break;
          case 'OPTION_FIELD':
            fieldComponent = (
              <div className="my-1 flex items-center">
                <RadioInputs
                  label={field.name}
                  onChange={() => {}}
                  id={field.name}
                  options={
                    field?.optionList ? field.optionList.split(',').map((o) => o.trim()) : []
                  }
                  optionDescriptions={
                    field?.optionDescriptions
                      ? field.optionDescriptions.split(';;').map((o) => o.trim())
                      : undefined
                  }
                  isRequired={field.isRequired}
                  selectedOption=""
                  className={field.isRow ? 'gap-2 flex items-center' : ''}
                />
              </div>
            );
            break;
          case 'DOCUMENT_FIELD':
            fieldComponent = (
              <div className="my-1 flex items-center w-full">
                <DocumentField
                  text={field.documentText || ''}
                  className={`w-full ${field.fontWeight} text-[${field.fontSize ?? '12'}]px ${
                    field.alignment === 'RIGHT'
                      ? 'text-right'
                      : field.alignment === 'CENTER'
                      ? 'text-center'
                      : 'text-left'
                  }`}
                />
              </div>
            );
            break;
          case 'INVITE_USER':
            fieldComponent = (
              <InviteSignature
                data={
                  field?.whoNeedsToSign
                    ? field.whoNeedsToSign.split(',').map((o) => ({
                        title: o.trim(),
                        email: '',
                        step: 0,
                        signatureGroupName: field.name,
                        placeHolder: o.trim(),
                        type: field.type ?? 'AUTHORIZED_AGENT',
                        minimumCreditAmount: field.minimumCreditAmount,
                        fields: field.invitedSignaturesFields || [],
                        minimumNumberOfSignatures: field.minimumNumberOfSignatures,
                        isRequired: field.isRequired,
                        id: field.id,
                      }))
                    : []
                }
                onChange={() => {}}
                value={[]}
              />
            );
            break;
          case 'CONDITIONAL_OPTION':
            fieldComponent = (
              <ConditionalOption
                optionType={field.optionType || 'text'}
                id={field.id}
                onChange={() => {}}
                isRequired={field.isRequired}
                label={field.name}
                selectedValue={{ value: '', secondaryValue: '' }}
                secondaryText={field.secondaryText}
                defaultOption={'Yes'}
              />
            );
            break;
          case 'BANK_REFERENCE':
            fieldComponent = (
              <AppBankReference
                onSubmit={() => {}}
                bankDetails={null}
                bankError={null}
                bankReferenceRequiredFields={[]}
                supplierName={currentSupplier?.name ?? 'Supplier'}
                checkRequired={field.isRequired}
                onlyShowPlaidOption={field.onlyShowPlaidOption}
              />
            );
            break;
          case 'STATE_DROPDOWN':
            fieldComponent = (
              <StateDropDown
                isRequired={field.isRequired}
                label={field.name}
                value={''}
                errorText={''}
                onTextChange={() => {}}
              />
            );
            break;
          case 'CHECKBOX_LIST':
            fieldComponent = (
              <CheckboxList
                isRequired={field.isRequired}
                label={field.name}
                options={field?.optionList ? field.optionList.split(',').map((o) => o.trim()) : []}
                value={''}
                errorText={''}
                onTextChange={() => {}}
              />
            );
            break;
          case 'DROPDOWN':
            fieldComponent = (
              <OptionsDropDown
                isRequired={field.isRequired}
                label={field.name}
                options={parseDropdownOptions(field?.optionList)}
                value={''}
                errorText={''}
                onTextChange={() => {}}
              />
            );
            break;
          default:
            fieldComponent = (
              <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
            );
        }

        return (
          <div key={idx} className="w-full min-w-0">
            <div className="px-2 py-2">{fieldComponent}</div>
          </div>
        );
      });
    };

    // ── section editor renderer ───────────────────────────────────────────────

    const renderSectionEditor = (option: string, section: SectionItem, sectionIndex: number) => {
      const sId = section.id;

      return (
        <div key={sId} className="w-full border border-gray-300 rounded-lg p-3 my-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600">
              Section {sectionIndex + 1} —{' '}
              {section.sectionType === 'GROUPED' ? 'Grouped' : 'Ungrouped Fields'}
            </span>
            <div
              className="flex items-center cursor-pointer text-red-500 text-xs"
              onClick={() => removeSection(option, sId)}>
              <span className="mr-1">Remove</span>
              <Trash color={COLORS.ERROR_400} size={14} />
            </div>
          </div>

          {section.sectionType === 'GROUPED' && (
            <>
              <AppInputField
                isRequired
                label="Group Name"
                placeholder="Enter group name"
                value={section.groupName || ''}
                errorText={errorFields.includes(`groupName-${sId}`) ? 'Group name is required' : ''}
                onTextChange={(text) => updateSection(option, sId, { groupName: text })}
              />
              {(section.groupedFields ?? []).map((group, i) => {
                const columns = getColumnsCount(group.columnType);
                const fieldsArray = Array(columns)
                  .fill(null)
                  .map(
                    (_, ci) =>
                      group.fields[ci] || {
                        name: '',
                        id: '',
                        fromField: 'TEXT' as const,
                        isRequired: false,
                        prefix: '',
                        suffix: '',
                      }
                  );

                return (
                  <div key={group.id} className="w-full rounded GREY_300-BORDER border p-2 my-2">
                    <AppDropDown
                      label="Number of Columns"
                      isRequired
                      value={group.columnType}
                      items={COLUMN_TYPES.map((c) => ({ label: c.replaceAll('_', ' '), value: c }))}
                      onSelectChange={(item) => {
                        if (item)
                          updateGroupColumnType(
                            option,
                            sId,
                            i,
                            item.value as 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS'
                          );
                      }}
                    />
                    <div
                      className={`flex gap-2 mt-2 ${
                        columns !== 1 ? 'flex-row items-start' : 'flex-col'
                      }`}>
                      {renderFieldInputs(option, sId, fieldsArray, i, true, (fIdx, key, val) =>
                        updateGroupField(option, sId, i, fIdx, key, val)
                      )}
                    </div>
                    {(section.groupedFields ?? []).length > 1 && (
                      <div
                        className="w-full cursor-pointer flex justify-end my-2 text-red-500 items-center"
                        onClick={() => removeGroupFromSection(option, sId, i)}>
                        <div className="text-sm mr-1">Remove</div>
                        <Trash color={COLORS.ERROR_400} size={16} />
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="my-2">
                <AppButton
                  text="Add Field"
                  onClick={() => addGroupToSection(option, sId)}
                  type="PRIMARY"
                  buttonStyles={{ width: '100px', height: '35px' }}
                />
              </div>
              <AppInputField
                label="Minimum Number of Groups"
                value={section.minimunNumberOfGroups?.toString() || ''}
                onTextChange={(text) =>
                  updateSection(option, sId, { minimunNumberOfGroups: Number(text) })
                }
                inputType="number"
                isRequired
                placeholder="Enter minimum number of groups"
                onBlur={() => {
                  if ((section.minimunNumberOfGroups ?? 1) < 1) {
                    updateSection(option, sId, { minimunNumberOfGroups: 1 });
                  }
                }}
              />
              <div className="my-1 flex items-center">
                <AppRadioButton
                  isChecked={section.isFieldArray ?? false}
                  onClick={(val: boolean) => updateSection(option, sId, { isFieldArray: val })}
                />
                <p
                  className="text-sm ml-1 cursor-pointer"
                  onClick={() =>
                    updateSection(option, sId, { isFieldArray: !section.isFieldArray })
                  }>
                  Has multiple fields
                </p>
              </div>
              {section.isFieldArray && (
                <AppInputField
                  label="More Button Name"
                  placeholder="Enter button name for adding more"
                  value={section.moreButtonName || ''}
                  onTextChange={(text) => updateSection(option, sId, { moreButtonName: text })}
                />
              )}
            </>
          )}

          {section.sectionType === 'NOT_GROUPED' && (
            <>
              <AppDropDown
                label="Number of Columns"
                isRequired
                value={section.columnType || ''}
                errorText={errorFields.includes(`fieldColumn-${sId}`) ? 'Please select' : ''}
                items={COLUMN_TYPES.map((type) => ({
                  label: type.replaceAll('_', ' '),
                  value: type,
                }))}
                onSelectChange={(item) => {
                  if (!item) return;
                  const columnCount = getColumnsCount(item.value);
                  const newFields = Array(columnCount)
                    .fill(null)
                    .map(() => ({
                      name: '',
                      id: '',
                      fromField: 'TEXT' as const,
                      isRequired: false,
                      prefix: '',
                      suffix: '',
                    }));
                  updateSection(option, sId, {
                    columnType: item.value as 'ONE_COLUMN' | 'TWO_COLUMNS' | 'THREE_COLUMNS',
                    unGroupedFields: newFields,
                  });
                }}
              />
              <div
                className={`flex gap-2 mt-2 ${
                  section.columnType !== 'ONE_COLUMN' ? 'flex-row items-start' : 'flex-col'
                }`}>
                {renderFieldInputs(
                  option,
                  sId,
                  section.unGroupedFields ||
                    Array(getColumnsCount(section.columnType || 'ONE_COLUMN'))
                      .fill(null)
                      .map(() => ({
                        name: '',
                        id: '',
                        fromField: 'TEXT',
                        isRequired: false,
                        prefix: '',
                        suffix: '',
                      })),
                  0,
                  false,
                  (fIdx, key, val) => updateUngroupedField(option, sId, fIdx, key, val)
                )}
              </div>
            </>
          )}
        </div>
      );
    };

    // ── main render ───────────────────────────────────────────────────────────

    return (
      <div className="w-full border rounded p-2 flex gap-2 flex-col border-gray-200 rounded-lg">
        {list.map((option, index) => (
          <div
            key={`conditional_selected_option_${index}`}
            className="flex items-center border-gray-200 border rounded-lg p-2">
            <div className="w-full">
              <div className="font-bold text-sm">{option}</div>
              <div className="flex gap-2 items-center">
                <AppRadioButton
                  isChecked={selectedFieldOption.includes(option)}
                  onClick={() => {
                    if (selectedFieldOption.includes(option)) {
                      setSelectedFieldOption(selectedFieldOption.filter((item) => item !== option));
                    } else {
                      setSelectedFieldOption([...selectedFieldOption, option]);
                    }
                  }}
                />
                <p className="text-xs">Does this field require additional information?</p>
              </div>

              {selectedFieldOption.includes(option) && (
                <div className="flex flex-col mt-2 p-2 border rounded-lg border-gray-200 w-full gap-2">
                  <div className="mb-1 text-xs">{`Add fields for ${option}`}</div>

                  {errorFields.includes(`${option}-no-sections`) && (
                    <p className="text-xs text-red-500">Please add at least one section</p>
                  )}

                  {/* Ordered sections */}
                  {(sections[option] ?? []).map((section, sIdx) =>
                    renderSectionEditor(option, section, sIdx)
                  )}

                  {/* Add section buttons */}
                  <div className="flex gap-2 mt-1">
                    <AppButton
                      text="+ Add Group Section"
                      onClick={() => addSection(option, 'GROUPED')}
                      type="SECONDARY"
                      buttonStyles={{ height: '32px', fontSize: '12px' }}
                    />
                    <AppButton
                      text="+ Add Ungrouped Fields"
                      onClick={() => addSection(option, 'NOT_GROUPED')}
                      type="SECONDARY"
                      buttonStyles={{ height: '32px', fontSize: '12px' }}
                    />
                  </div>

                  {(sections[option] ?? []).length >= 2 && (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-gray-500 whitespace-nowrap">
                        Space between sections
                      </p>
                      <div className="flex gap-1">
                        {(['none', 'sm', 'md', 'lg', 'xl'] as const).map((size) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => setSectionGap((prev) => ({ ...prev, [option]: size }))}
                            className={`px-2 py-0.5 text-xs rounded border ${
                              (sectionGap[option] ?? 'sm') === size
                                ? 'border-blue-500 bg-blue-50 text-blue-600 font-medium'
                                : 'border-gray-300 text-gray-500'
                            }`}>
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Preview */}
        <p className="text-sm mb-[0.5]">Preview</p>
        <div className="border border-blue-500 p-2">
          {list.map((option, index) => {
            const optionSections = sections[option] ?? [];
            return (
              <div key={`conditional_selected_option_preview_${index}`} className="mb-2">
                <p className="text-sm mb-1">
                  Preview for <span className="font-bold">{option}</span>
                </p>
                {selectedFieldOption.includes(option) ? (
                  <div>
                    {optionSections.map((section, sIdx) => (
                      <div key={section.id} className="border border-blue-300 my-2 p-1">
                        <p className="text-xs text-gray-400 mb-1">
                          Section {sIdx + 1} —{' '}
                          {section.sectionType === 'GROUPED' ? 'Grouped' : 'Ungrouped'}
                        </p>
                        {section.sectionType === 'GROUPED' && (
                          <div className="border border-blue-500 my-2">
                            <p className="text-sm font-bold ml-2">{section.groupName}</p>
                            {sendEmail[option] && (
                              <div className="flex flex-row-reverse">
                                <AppButton
                                  text="Send Email"
                                  onClick={() => {}}
                                  type="SECONDARY"
                                  icon={<Mail className={COLORS.PRIMARY_500} size={16} />}
                                  buttonStyles={{
                                    height: '35px',
                                    alignItems: 'center',
                                    display: 'flex',
                                  }}
                                />
                              </div>
                            )}
                            {(section.groupedFields ?? []).map((group) => {
                              const columns = getColumnsCount(group.columnType);
                              const fieldsArray = Array(columns)
                                .fill(null)
                                .map(
                                  (_, ci) =>
                                    group.fields[ci] || {
                                      name: '',
                                      id: '',
                                      fromField: 'TEXT',
                                      isRequired: false,
                                      prefix: '',
                                      suffix: '',
                                    }
                                );
                              return (
                                <div key={group.id} className="w-full">
                                  <div className="flex gap-2">{renderPreview(fieldsArray)}</div>
                                </div>
                              );
                            })}
                            {section.isFieldArray && (
                              <div className="w-full flex mb-2 justify-end">
                                <AppButton
                                  text={section.moreButtonName || `Add ${section.groupName}`}
                                  onClick={() => {}}
                                  icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                                  buttonStyles={{ height: '30px', borderRadius: '5px' }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {section.sectionType === 'NOT_GROUPED' && (
                          <div
                            className="grid gap-2 mt-2 border border-blue-500"
                            style={{
                              gridTemplateColumns: `repeat(${getColumnsCount(
                                section.columnType || 'ONE_COLUMN'
                              )}, minmax(0, 1fr))`,
                            }}>
                            {renderPreview(section.unGroupedFields ?? [])}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="my-2 TEXT_SECONDARY-CLR text-sm">No Preview for {option}</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
);

export default ConditionalSelectedOptionFields;
